import ThemeTableSimple2 from 'assets/components/table/themes/Theme.Table.Simple.2';
import { getLocales } from 'assets/locales/Locale';
import { formatCurrency, formatDeduction, formatSignedCurrency, roundToStep } from 'assets/utils/data/Number';
import { isNil } from 'lodash';
import { ExcludedAmount } from './ExcludedAmount';

type Props = {
  contract?: Model.IContract;
  contractRefund: Model.IContractRefund;
};

type SummaryRow = {
  label: string;
  valueAtSale?: number;
  valueRetained?: number;
  // valueRetainedDisplay?: string;
  refundAmount?: number;
  refundAmountDisplay?: string;
  isRefundExcluded?: boolean;
  isTotal?: boolean;
};

const ContractRefundPriceSummariesTable = ({ contract, contractRefund }: Props) => {
  const { lang } = getLocales();
  // Old formula: DealerProtectProfitAmount + (Dealer Profit Amount - DealerProtectProfitAmount) * Proration Factor
  // const refundConfigTemplate = contractRefund.refundConfigTemplateJson ?? contractRefund.refundConfigTemplate;
  // const protectedAmount = refundConfigTemplate?.dealerProtectProfitAmount;
  // const dealerProtectProfitExpirationDays = refundConfigTemplate?.dealerProtectProfitExpirationDays;
  // const shouldShowDealerProfitSplit =
  //   !isNil(protectedAmount) &&
  //   !isNil(dealerProtectProfitExpirationDays) &&
  //   contractRefund.usedTermInDays >= dealerProtectProfitExpirationDays;
  // const dealerProfitAfterProtection = isNil(protectedAmount)
  //   ? undefined
  //   : Math.max(contractRefund.dealerProfitValueAtSale - protectedAmount, 0);
  // const proratedRetainedAmount = isNil(dealerProfitAfterProtection)
  //   ? undefined
  //   : dealerProfitAfterProtection * contractRefund.retainFactor;
  // const dealerProfitRetainedSplitDisplay =
  //   shouldShowDealerProfitSplit && !isNil(proratedRetainedAmount)
  //     ? `${formatCurrency(protectedAmount)} + ${formatCurrency(proratedRetainedAmount)}`
  //     : undefined;

  const taxLabel = (amount?: number, tax?: number) => {
    if (!amount || tax === undefined || tax === null) return lang.tax.toUpperCase();

    const rate = Number(((Math.abs(tax) / Math.abs(amount)) * 100).toFixed(2));
    return `${lang.tax.toUpperCase()} (${contract.client?.taxExempt ? lang.exempt.toUpperCase() : rate}%)`;
  };

  const contractTaxLabel = (...rates: Array<number | undefined>) => {
    if (rates.some((rate) => rate === undefined || rate === null)) return lang.tax.toUpperCase();

    const rate = rates.reduce((total, current) => total + (current ?? 0), 0) * 100;
    return `${lang.tax.toUpperCase()} (${contract.client?.taxExempt ? lang.exempt.toUpperCase() : rate.toFixed(2)}%)`;
  };

  const rows: SummaryRow[] = [
    {
      label: lang.dealerCostAmount,
      valueAtSale: roundToStep(contract?.dealerCost, 0.01),
      valueRetained: contractRefund.retainedDealerCostAmt,
      refundAmount: contractRefund.refundedDealerCostAmt,
    },
    {
      label: `${lang.dealerCost} ${contractTaxLabel(
        contract?.dealerCostProvincialSalesTax,
        contract?.dealerCostGstHst
      )}`,
      valueAtSale: roundToStep(contractRefund.dealerCostTaxAtSale, 0.01),
      valueRetained: contractRefund.retainedDealerCostTaxAmt,
      refundAmount: contractRefund.refundedDealerCostTaxAmt,
    },
    {
      label: lang.dealerCostTotal,
      valueAtSale: roundToStep(contractRefund.dealerCostValueAtSale, 0.01),
      valueRetained: contractRefund.retainedDealerCostTotal,
      refundAmount: contractRefund.refundedDealerCostTotal,
      isTotal: true,
    },
    {
      label: lang.dealerProfitAmount,
      valueAtSale: roundToStep(contract?.dealerProfit, 0.01),
      valueRetained: contractRefund.retainedASCDealerProfitAmt,
      refundAmount: contractRefund.refundedASCDealerProfitAmt,
      isRefundExcluded: contractRefund.isRefundedASCDealerProfitTotalExcluded,
    },
    {
      label: `${lang.dealerProfit} ${contractTaxLabel(
        contract?.dealerProfitProvincialSalesTax,
        contract?.dealerProfitGstHst
      )}`,
      valueAtSale: roundToStep(contractRefund.dealerProfitTaxAtSale, 0.01),
      valueRetained: contractRefund.retainedASCDealerProfitTaxAmt,
      refundAmount: contractRefund.refundedASCDealerProfitTaxAmt,
      isRefundExcluded: contractRefund.isRefundedASCDealerProfitTotalExcluded,
    },
    {
      label: lang.dealerProfitTotal,
      valueAtSale: roundToStep(contractRefund.dealerProfitValueAtSale, 0.01),
      valueRetained: contractRefund.retainedASCDealerProfitTotal,
      refundAmount: contractRefund.refundedASCDealerProfitTotal,
      isRefundExcluded: contractRefund.isRefundedASCDealerProfitTotalExcluded,
      isTotal: true,
    },
    {
      label: lang.dealerProtectedProfitAmount,
      refundAmountDisplay: isNil(contractRefund.dealerProtectedProfitAmount)
        ? undefined
        : `-${formatCurrency(contractRefund.dealerProtectedProfitAmount)}`,
    },
    {
      label: lang.dealerResponsiblePortionAmount,
      valueAtSale: roundToStep(contract?.dealerProfit, 0.01),
      valueRetained: contractRefund.retainedDealerProfitAmt,
      // valueRetainedDisplay: dealerProfitRetainedSplitDisplay,
      refundAmount: contractRefund.refundedDealerProfitAmt,
      isRefundExcluded: contractRefund.isRefundedDealerProfitTotalExcluded,
    },
    {
      label: `${lang.dealerResponsiblePortion} ${contractTaxLabel(
        contract?.dealerProfitProvincialSalesTax,
        contract?.dealerProfitGstHst
      )}`,
      valueAtSale: roundToStep(contractRefund.dealerProfitTaxAtSale, 0.01),
      valueRetained: contractRefund.retainedDealerProfitTaxAmt,
      refundAmount: contractRefund.refundedDealerProfitTaxAmt,
      isRefundExcluded: contractRefund.isRefundedDealerProfitTotalExcluded,
    },
    {
      label: lang.dealerResponsiblePortionTotal,
      valueAtSale: roundToStep(contractRefund.dealerProfitValueAtSale, 0.01),
      valueRetained: contractRefund.retainedDealerProfitTotal,
      refundAmount: contractRefund.refundedDealerProfitTotal,
      isRefundExcluded: contractRefund.isRefundedDealerProfitTotalExcluded,
      isTotal: true,
    },
    {
      label: lang.retailAmount,
      valueAtSale: roundToStep(contract?.salePriceCAD, 0.01),
      valueRetained: contractRefund.totalRetainedDealerAmt,
      refundAmount: contractRefund.totalRefundedDealerAmt,
    },
    {
      label: `${lang.retail} ${taxLabel(contract?.salePriceCAD, contractRefund.retailSalePriceTaxValueAtSale)}`,
      valueAtSale: roundToStep(contractRefund.retailSalePriceTaxValueAtSale, 0.01),
      valueRetained: contractRefund.totalRetainedDealerTaxAmt,
      refundAmount: contractRefund.totalRefundedDealerTaxAmt,
    },
    {
      label: lang.retailSalePriceTotal,
      valueAtSale: roundToStep(contractRefund.retailSalePriceValueAtSale, 0.01),
      valueRetained: contractRefund.retainedDealerTotal,
      refundAmount: contractRefund.refundedDealerTotal,
      isTotal: true,
    },
  ];

  return (
    <table className={ThemeTableSimple2}>
      <thead>
        <tr>
          <th></th>
          <th>{lang.valueAtSales}</th>
          <th>{lang.valueRetained}</th>
          <th>{lang.refundAmount}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.label}>
            <td style={row.isTotal ? { fontWeight: 'bold' } : undefined}>{row.label}</td>
            <td style={row.isTotal ? { fontWeight: 'bold' } : undefined}>{formatCurrency(row.valueAtSale)}</td>
            <td style={row.isTotal ? { fontWeight: 'bold' } : undefined}>{formatCurrency(row.valueRetained)}</td>
            <td style={row.isTotal ? { fontWeight: 'bold' } : undefined}>
              <ExcludedAmount isExcluded={row.isRefundExcluded}>
                {row.refundAmountDisplay ?? formatCurrency(row.refundAmount)}
              </ExcludedAmount>
            </td>
          </tr>
        ))}
        <tr>
          <td>
            <div></div>
          </td>
          <td>
            <div></div>
          </td>
          <td>
            <div></div>
          </td>
          <td>
            <div></div>
          </td>
        </tr>
        <tr>
          <td>{lang.totalDeductions}</td>
          <td></td>
          <td></td>
          <td>{formatDeduction(contractRefund.totalRefundDeductions)}</td>
        </tr>
        <tr>
          <td style={{ fontWeight: 'bold' }}>{lang.totalCustomerRefund}</td>
          <td></td>
          <td></td>
          <td style={{ fontWeight: 'bold' }}>{formatSignedCurrency(contractRefund.totalToBeRefunded)}</td>
        </tr>
      </tbody>
    </table>
  );
};

export default ContractRefundPriceSummariesTable;
